import { useState } from 'react';
import { GridColDef, GridRowModes, GridRowModesModel } from '@mui/x-data-grid';
import { OverflowMenuItem } from 'shared/components/overflow-menu/overflow-menu';

import { DataGridOverflowMenuItem } from '../data-grid';

export type DataGridProps<DataType extends object> = {
  data: DataType[];
  columns: GridColDef[];
  rowModesModel: GridRowModesModel;
  overflowMenuOptions?: Array<DataGridOverflowMenuItem<DataType>>;
  editButtons?: Array<React.JSX.Element>;
};

type ActionCellState = {
  isEditMode: boolean;
  showOverflowMenu: boolean;
};

type DataGrid<DataType extends object> = {
  isOverflowMenuOpen: boolean;
  anchorEl: HTMLElement | null;
  clickedRow: DataType | null;
  closeOverflowMenu: () => void;
  mapMenuItems: (menuItems: Array<DataGridOverflowMenuItem<DataType>>, rowData: DataType) => Array<OverflowMenuItem>;
  getActionCellState: (rowId: string) => ActionCellState;
  onMoreClick: (event: React.MouseEvent<HTMLElement, MouseEvent>, row: DataType) => void;
};

export const useDataGrid = <DataType extends object>({
  overflowMenuOptions,
  rowModesModel,
}: DataGridProps<DataType>): DataGrid<DataType> => {
  const [isOverflowMenuOpen, setIsOverflowMenuOpen] = useState(false);
  const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);
  const [clickedRow, setClickedRow] = useState<DataType | null>(null);

  const closeOverflowMenu = (): void => {
    setIsOverflowMenuOpen(false);
    setClickedRow(null);
  };

  const menuItemIsVisible = (rowData: DataType, isVisible?: ((item: DataType) => boolean) | undefined): boolean =>
    isVisible ? isVisible(rowData) : true;

  const mapMenuItems = (menuItems: Array<DataGridOverflowMenuItem<DataType>>, rowData: DataType): Array<OverflowMenuItem> =>
    menuItems
      .filter((item) => (typeof item === 'string' ? true : menuItemIsVisible(rowData, item.isVisible)))
      .map((item) => mapMenuItem(item, rowData));

  const mapMenuItem = (item: DataGridOverflowMenuItem<DataType>, rowData: DataType): OverflowMenuItem => {
    if (item.label === 'divider') {
      return 'divider';
    }
    return {
      label: item.label,
      action: () => item.action?.(rowData),
      disabled: item.disabled?.(rowData) ?? false,
    };
  };

  const getActionCellState = (rowId: string): { isEditMode: boolean; showOverflowMenu: boolean } => {
    const isEditMode = rowModesModel[rowId]?.mode === GridRowModes.Edit;
    return {
      isEditMode,
      showOverflowMenu: !!overflowMenuOptions,
    };
  };

  const onMoreClick = (event: React.MouseEvent<HTMLElement>, row: DataType): void => {
    event.stopPropagation();
    setAnchorEl(event.currentTarget);
    setIsOverflowMenuOpen(true);
    setClickedRow(row);
  };

  return {
    isOverflowMenuOpen,
    anchorEl,
    clickedRow,
    closeOverflowMenu,
    mapMenuItems,
    getActionCellState,
    onMoreClick,
  };
};
